import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
} from "./common";

/**
 * Identifier schemas for participant-to-registered-user mapping
 * Enables linking anonymous participants to authenticated users
 */

const DeviceInfo = z.object({
  userAgent: z.string(),
  platform: z.string(),
  fingerprint: z.string().optional(),
});

const LinkSource = z.enum(["login", "signup", "manual", "migration"]);

// Mappings are isolated per account, keyed (accountId, participantId) (R69 Q4b/Q6a).
const ParticipantUserMapping = z.object({
  accountId: UuidField,
  participantId: ParticipantIdField,
  userId: UuidField,
  linkedAt: TimestampField,
  linkSource: LinkSource,
  deviceInfo: DeviceInfo.optional(),
});

const LinkParticipantRequest = z.object({
  participantId: ParticipantIdField,
  userId: UuidField,
  linkSource: LinkSource,
  deviceInfo: DeviceInfo.optional(),
});

const LinkParticipantResponse = z.object({
  success: z.literal(true),
  mapping: ParticipantUserMapping,
});

const UnlinkParticipantRequest = z.object({
  participantId: ParticipantIdField,
});

const UnlinkParticipantResponse = z.object({
  success: z.literal(true),
  message: z.string(),
});

const GetUserByParticipantRequest = z.object({
  participantId: ParticipantIdField,
});

const GetUserByParticipantResponse = z.object({
  success: z.literal(true),
  userId: UuidField.nullable(),
  mapping: ParticipantUserMapping.nullable(),
});

const GetParticipantsByUserRequest = z.object({
  userId: UuidField,
});

const GetParticipantsByUserResponse = z.object({
  success: z.literal(true),
  participantIds: z.array(z.string()),
  mappings: z.array(ParticipantUserMapping),
  total: z.number().int(),
});

const MappingConflictError = z.object({
  success: z.literal(false),
  error: z.literal("MAPPING_CONFLICT"),
  message: z.string(),
  details: z.object({
    participantId: z.string(),
    existingUserId: UuidField,
    requestedUserId: UuidField,
    linkedAt: TimestampField,
  }),
});

export const schemas = {
  DeviceInfo,
  LinkSource,
  ParticipantUserMapping,
  LinkParticipantRequest,
  LinkParticipantResponse,
  UnlinkParticipantRequest,
  UnlinkParticipantResponse,
  GetUserByParticipantRequest,
  GetUserByParticipantResponse,
  GetParticipantsByUserRequest,
  GetParticipantsByUserResponse,
  MappingConflictError,
} as const;

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenient access
export type * from "zod";

